import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Table, Modal, Form, Input, Select, DatePicker, message, Drawer, List, Checkbox,
  Space, Tag, Progress, Row, Col, Statistic, Radio, Tooltip, Upload, Typography, Popconfirm, ConfigProvider
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CheckCircleOutlined,
  ClockCircleOutlined, CloseCircleOutlined, UploadOutlined, FileTextOutlined,
  ExclamationCircleOutlined, WarningOutlined, ReloadOutlined, InfoCircleOutlined,
  AlertOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import dayjs from 'dayjs';
import DepartmentSelect from '../components/DepartmentSelect';
import PositionSelect from '../components/PositionSelect';
import AttachmentUpload from '../components/AttachmentUpload';
import PermissionGuard from '../components/PermissionGuard';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const Onboarding = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [onboarding, setOnboarding] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOnboarding, setEditingOnboarding] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    department_id: '',
    view_mode: 'all',
    onboarding_id: '',
    dateRange: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [form] = Form.useForm();
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [statusModal, setStatusModal] = useState({ visible: false, targetStatus: '', notes: '' });
  const [confirmArrivalModal, setConfirmArrivalModal] = useState({ visible: false });
  const [textTaskModal, setTextTaskModal] = useState({ visible: false, taskIndex: null, notes: '', isEdit: false });
  const [fileTaskModal, setFileTaskModal] = useState({ 
    visible: false, 
    taskIndex: null, 
    uploadedFiles: [], // 使用AttachmentUpload组件的文件格式
    notes: '',
    isEdit: false 
  });
  const [highlightedOnboardingId, setHighlightedOnboardingId] = useState(null);
  const [addTaskModal, setAddTaskModal] = useState({ 
    visible: false, 
    availableTasks: [], 
    selectedTasks: [],
    loading: false 
  });

  // 检查是否有从线索详情页面传递过来的搜索参数
  useEffect(() => {
    if (location.state) {
      const { searchOnboardingId, highlightOnboarding } = location.state;
      if (searchOnboardingId && highlightOnboarding) {
        console.log('🔍 接收到入职记录搜索参数:', searchOnboardingId);
        setHighlightedOnboardingId(searchOnboardingId);
        
        // 自动设置入职ID筛选
        setFilters(prev => ({
          ...prev,
          onboarding_id: searchOnboardingId.toString()
        }));
        
        // 清空location.state，避免重复处理
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state]);

  // 统一将任务字段规范为数组，并过滤掉已删除的任务
  const toTasksArray = (tasks) => {
    let taskArray = [];
    
    if (Array.isArray(tasks)) {
      taskArray = tasks;
    } else if (typeof tasks === 'string') {
      try {
        const parsed = JSON.parse(tasks);
        taskArray = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('JSON解析失败:', error);
        return [];
      }
    } else {
      return [];
    }
    
    // 过滤掉已删除的任务
    return taskArray.filter(task => {
      // 如果任务有deleted_at字段且不为空，则过滤掉
      if (task.deleted_at && task.deleted_at.trim() !== '') {
        return false;
      }
      // 如果任务有deleted字段且为true，则过滤掉
      if (task.deleted === true) {
        return false;
      }
      // 如果任务名称为空或无效，则过滤掉
      if (!task.name || task.name.trim() === '') {
        return false;
      }
      return true;
    });
  };

  // 默认入职任务模板
  const defaultTasks = [
    { name: '办理入职手续', description: '完成入职相关手续办理', status: 'pending' },
    { name: '领取工作用品', description: '领取电脑、工牌等工作用品', status: 'pending' },
    { name: '参加入职培训', description: '参加公司入职培训', status: 'pending' },
    { name: '熟悉工作环境', description: '熟悉办公环境和同事', status: 'pending' },
    { name: '部门介绍', description: '与部门同事见面，了解工作内容', status: 'pending' }
  ];

  useEffect(() => {
    fetchOnboarding();
    fetchGlobalStats(); // 获取全局统计数据
    fetchOverviewStats(); // 获取概览统计数据
  }, [filters, pagination.current, pagination.pageSize]);
  
  // 获取全局统计数据
  const fetchGlobalStats = async () => {
    try {
      const response = await fetch('/api/onboarding/stats/overview', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('📊 全局统计数据:', result);
        
        // 处理状态统计数据
        const statusMap = {
          total: result.total || 0,
          pending: 0,
          inProgress: 0,
          completed: 0,
          cancelled: 0,
          overdue: 0
        };
        
        // 从statusStats中提取各状态的数量
        if (result.statusStats) {
          result.statusStats.forEach(stat => {
            if (stat.status === 'pending') statusMap.pending = stat.count;
            else if (stat.status === 'in_progress') statusMap.inProgress = stat.count;
            else if (stat.status === 'completed') statusMap.completed = stat.count;
            else if (stat.status === 'cancelled') statusMap.cancelled = stat.count;
          });
        }
        
        setGlobalStats(statusMap);
      }
    } catch (error) {
      console.error('获取全局统计失败:', error);
    }
  };

  // 获取概览统计数据
  const fetchOverviewStats = async () => {
    try {
      const response = await fetch('/api/onboarding/stats/monthly-weekly', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('📊 概览统计数据:', result);
        setOverviewStats(result);
      }
    } catch (error) {
      console.error('获取概览统计失败:', error);
    }
  };

  const fetchOnboarding = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      console.log('🔍 开始构建查询参数，当前筛选条件:', filters);
      console.log('🔍 当前分页参数:', pagination);
      
      // 添加分页参数
      params.append('page', pagination.current);
      params.append('limit', pagination.pageSize);
      console.log('🔍 添加分页参数:', { page: pagination.current, limit: pagination.pageSize });
      
      if (filters.search) {
        params.append('search', filters.search);
        console.log('🔍 添加搜索参数:', filters.search);
      }
      
      // 特殊处理：如果状态是"overdue"，不传递给后端，由前端筛选
      if (filters.status && filters.status !== 'overdue') {
        params.append('status', filters.status);
        console.log('🔍 添加状态参数:', filters.status);
      } else if (filters.status === 'overdue') {
        console.log('🔍 状态为已过期，将由前端筛选');
      }
      
      if (filters.department_id) {
        params.append('department_id', filters.department_id);
        console.log('🔍 添加部门参数:', filters.department_id);
      }
      if (filters.onboarding_id) {
        params.append('onboarding_id', filters.onboarding_id);
        console.log('🔍 添加入职记录ID参数:', filters.onboarding_id);
      }

      // 添加时间范围参数
      if (filters.dateRange && filters.dateRange.length === 2) {
        const [startDate, endDate] = filters.dateRange;
        params.append('start_date', startDate.format('YYYY-MM-DD'));
        params.append('end_date', endDate.format('YYYY-MM-DD'));
        console.log('🔍 添加时间范围参数:', {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD')
        });
      }

      // 根据用户权限和筛选模式决定是否只显示自己的记录
      // 注意：当使用ID筛选时，应该忽略用户权限限制，因为用户可能通过链接跳转查看特定记录
      if ((filters.view_mode === 'my' || !isAdmin()) && !filters.onboarding_id) {
        params.append('created_by_id', user.id);
        console.log('🔍 添加创建者参数:', user.id);
      } else if (filters.onboarding_id) {
        console.log('🔍 使用ID筛选，忽略用户权限限制');
      }

      const queryString = params.toString();
      console.log('🔍 最终查询字符串:', queryString);
      console.log('🔍 请求URL:', `/onboarding?${queryString}`);

      const response = await api.get(`/onboarding?${queryString}`);
      console.log('🔍 API响应:', response.data);
      
      let onboardingData = [];
      
      // 修复：正确处理后端API返回的数据结构
      // 后端返回: { onboardings: [...], pagination: {...} }
      // 前端期望: 直接是数组
      if (response.data && response.data.onboardings) {
        console.log('📋 入职数据样本:', response.data.onboardings.slice(0, 2));
        onboardingData = response.data.onboardings;
        
        // 更新分页信息
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.data.pagination.total,
            current: response.data.pagination.page,
            pageSize: response.data.pagination.limit
          }));
          console.log('📊 更新分页信息:', response.data.pagination);
        }
      } else {
        console.log('📋 入职数据样本:', response.data?.slice(0, 2));
        onboardingData = response.data || [];
      }
      
      // 如果状态筛选是"overdue"，进行客户端筛选
      if (filters.status === 'overdue') {
        console.log('🔍 开始客户端过期筛选，原始数据量:', onboardingData.length);
        onboardingData = onboardingData.filter(o => {
          // 过期条件：状态不是completed/cancelled且入职日期<=今天
          if (o.status === 'completed' || o.status === 'cancelled') return false;
          if (!o.start_date) return false;
          
          const startDate = o.start_date;
          const today = new Date().toISOString().split('T')[0];
          const isOverdue = startDate <= today;
          
          if (isOverdue) {
            console.log(`✅ 过期工单筛选 ID:${o.id} - ${o.employee_name}`);
          }
          
          return isOverdue;
        });
        console.log('🔍 过期筛选后数据量:', onboardingData.length);
      }
      
      setOnboarding(onboardingData);
    } catch (error) {
      console.error('获取入职流程失败:', error);
      message.error('获取入职流程失败');
    } finally {
      setLoading(false);
    }
  };

  // 判断是否为管理员
  const isAdmin = () => {
    return user?.role === 'super_admin' || user?.role === 'admin';
  };

  // 处理分页变化
  const handleTableChange = (paginationInfo) => {
    console.log('🔍 分页变化:', paginationInfo);
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }));
  };

  const handleAdd = () => {
    setEditingOnboarding(null);
    setSelectedDepartmentId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingOnboarding(record);
    // 设置部门ID，用于职位联动
    setSelectedDepartmentId(record.department_id);
    form.setFieldsValue({
      employee_name: record.employee_name,
      department_id: record.department_id,
      position_id: record.position_id,
      start_date: record.start_date ? dayjs(record.start_date) : null
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    setSelectedOnboarding(record);
    setDrawerVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/onboarding/${id}`);
      message.success('删除成功');
      fetchOnboarding();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      // 获取部门和职位名称
      let departmentName = null;
      let positionName = null;
      
      if (values.department_id) {
        try {
          const deptResponse = await api.get(`/departments/${values.department_id}`);
          departmentName = deptResponse.data.name;
        } catch (deptError) {
          console.error('获取部门名称失败:', deptError);
        }
      }
      
      if (values.position_id) {
        try {
          const posResponse = await api.get(`/positions/${values.position_id}`);
          positionName = posResponse.data.name;
        } catch (posError) {
          console.error('获取职位名称失败:', posError);
        }
      }
      
      const data = {
        ...values,
        department: departmentName,        // 部门名称
        position: positionName,            // 职位名称
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null
      };

      let response;
      if (editingOnboarding) {
        response = await api.put(`/onboarding/${editingOnboarding.id}`, data);
        message.success('更新成功');
      } else {
        response = await api.post('/onboarding', data);
        message.success('创建成功');
        
        // 创建入职流程后，生成线索跟进记录
        if (response.data && response.data.lead_id) {
          await generateFollowUpRecord(response.data.lead_id, 'onboarding_created', {
            employee_name: data.employee_name,
            position: positionName,
            onboarding_id: response.data.id
          });
        }
      }

      setModalVisible(false);
      fetchOnboarding();
    } catch (error) {
      console.error('操作失败:', error);
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleTaskStatusChange = async (onboardingId, taskIndex, status) => {
    try {
      await api.put(`/onboarding/${onboardingId}/tasks`, {
        task_index: taskIndex,
        status: status
      });
      message.success('任务状态更新成功');
      fetchOnboarding();
    } catch (error) {
      console.error('更新任务状态失败:', error);
      message.error('更新任务状态失败');
    }
  };

  const refreshSelected = async (id) => {
    try {
      const res = await api.get(`/onboarding/${id}`);
      
      if (res.data) {
        // 检查数据结构，处理嵌套的onboarding属性
        let onboardingData = res.data;
        if (res.data.onboarding && typeof res.data.onboarding === 'object') {
          onboardingData = res.data.onboarding;
        }
        
        // 检查onboarding_tasks字段
        if (onboardingData.onboarding_tasks) {
          try {
            const parsedTasks = JSON.parse(onboardingData.onboarding_tasks);
            // 将解析后的任务数据保存回对象中，确保包含completed_at等字段
            onboardingData.onboarding_tasks = parsedTasks;
          } catch (parseError) {
            console.error('解析onboarding_tasks失败:', parseError);
          }
        }
        
        setSelectedOnboarding(onboardingData);
      } else {
        // 如果刷新失败，尝试从列表中找到对应记录
        const currentOnboarding = onboarding.find(item => item.id === id);
        if (currentOnboarding) {
          setSelectedOnboarding(currentOnboarding);
        }
      }
    } catch (error) {
      console.error('刷新入职数据失败:', error);
      // 如果刷新失败，尝试从列表中找到对应记录
      const currentOnboarding = onboarding.find(item => item.id === id);
      if (currentOnboarding) {
        setSelectedOnboarding(currentOnboarding);
      }
    }
  };

  const handleUpdateStatus = async (targetStatus, notes) => {
    if (!selectedOnboarding) return;

    // 如果是毁单状态，必须填写理由
    if (targetStatus === 'cancelled' && (!notes || notes.trim() === '')) {
      message.error('毁单时必须填写毁单理由');
      return;
    }

    try {
      await api.put(`/onboarding/${selectedOnboarding.id}`, { status: targetStatus, notes: notes || '' });
      message.success('入职状态已更新');
      setStatusModal({ visible: false, targetStatus: '', notes: '' });
      await fetchOnboarding();
      await refreshSelected(selectedOnboarding.id);
      
      // 入职状态变更后，生成线索跟进记录
      if (selectedOnboarding.lead_id) {
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'onboarding_status_changed', {
          new_status: getStatusText(targetStatus),
          onboarding_id: selectedOnboarding.id
        });
      }
    } catch (error) {
      console.error('更新入职状态失败:', error);
      message.error('更新入职状态失败');
    }
  };

  const openTextTaskModal = (taskIndex) => {
    // 获取当前任务的已保存记录信息
    const currentTask = toTasksArray(selectedOnboarding?.onboarding_tasks)[taskIndex];
    let existingNotes = '';
    let isEditMode = false;
    
    if (currentTask && currentTask.completion_notes && currentTask.completion_notes.trim()) {
      // 如果已有记录，加载到编辑模式
      existingNotes = currentTask.completion_notes;
      isEditMode = true;
    }
    
    // 设置Modal状态，支持编辑模式
    setTextTaskModal({ 
      visible: true, 
      taskIndex, 
      notes: existingNotes, 
      isEdit: isEditMode 
    });
    

  };

  // 保存文本任务记录（不完成任务）
  const saveTextTaskDraft = async () => {
    if (!selectedOnboarding) return;
    const { taskIndex, notes } = textTaskModal;
    
    if (!notes || !notes.trim()) {
      message.error('请填写任务记录');
      return;
    }
    
    try {
      // 只保存任务记录，不改变任务状态
      await api.put(`/onboarding/${selectedOnboarding.id}/tasks`, {
        task_index: taskIndex,
        completion_notes: notes.trim()
      });
      
      message.success('任务记录已保存，请点击"完成任务"按钮完成任务！');
      
      // 手动更新本地状态，确保记录信息立即显示
      if (selectedOnboarding && selectedOnboarding.onboarding_tasks) {
        try {
          const tasks = toTasksArray(selectedOnboarding.onboarding_tasks);
          if (tasks[taskIndex]) {
            // 更新任务的completion_notes字段
            tasks[taskIndex].completion_notes = notes.trim();
            // 强制重新渲染
            setSelectedOnboarding({ ...selectedOnboarding });
          }
        } catch (error) {
          console.error('手动更新本地状态失败:', error);
        }
      }
      
      // 关闭模态框并重置状态
      setTextTaskModal({ visible: false, taskIndex: null, notes: '', isEdit: false });
      
      // 刷新数据以显示新状态
      await refreshSelected(selectedOnboarding.id);
      
      // 生成线索跟进记录 - 任务记录保存
      if (selectedOnboarding?.lead_id) {
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'task_record_saved', {
          task_name: toTasksArray(selectedOnboarding?.onboarding_tasks)[taskIndex]?.name,
          onboarding_id: selectedOnboarding.id,
          record_type: 'onboarding_task',
          has_notes: true
        });
      }
    } catch (e) {
      console.error('保存失败:', e);
      message.error('保存失败: ' + (e.response?.data?.message || e.message || '未知错误'));
    }
  };

  // 保存文件（不完成任务）
  const saveFileTaskDraft = async () => {
    if (!selectedOnboarding) return;
    const { taskIndex, uploadedFiles } = fileTaskModal;
    
    if (uploadedFiles.length === 0) {
      message.error('请先上传文件');
      return;
    }
    
    if (!selectedOnboarding.id) {
      message.error('入职记录ID缺失');
      return;
    }
    
    if (taskIndex === null || taskIndex === undefined) {
      message.error('任务索引缺失');
      return;
    }
    
    try {
      // 从AttachmentUpload组件的文件格式中提取文件URLs
      const fileUrls = uploadedFiles.map(file => 
        file.url || (file.fileData ? `/api/upload/download/${file.fileData.id}` : '')
      ).filter(url => url);
      
      // 只保存文件信息，不改变任务状态
      await api.put(`/onboarding/${selectedOnboarding.id}/tasks`, {
        task_index: taskIndex,
        file_urls: fileUrls,
        completion_notes: fileTaskModal.notes || ''
      });
      message.success('文件已保存！');
      
      // 关闭模态框并强制重置状态
      setFileTaskModal({ 
        visible: false, 
        taskIndex: null, 
        uploadedFiles: [], 
        notes: '', 
        isEdit: false 
      });

      
      // 手动更新本地状态，确保文件信息立即显示
      if (selectedOnboarding && selectedOnboarding.onboarding_tasks) {
        try {
          const tasks = toTasksArray(selectedOnboarding.onboarding_tasks);
          if (tasks[taskIndex]) {
            // 更新任务的file_urls字段
            tasks[taskIndex].file_urls = fileUrls;
            // 强制重新渲染
            setSelectedOnboarding({ ...selectedOnboarding });
          }
        } catch (error) {
          console.error('手动更新本地状态失败:', error);
        }
      }
      
      // 刷新数据以显示新状态
      await refreshSelected(selectedOnboarding.id);
      
      // 生成线索跟进记录 - 文件上传
      if (selectedOnboarding?.lead_id) {
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'file_uploaded', {
          task_name: toTasksArray(selectedOnboarding?.onboarding_tasks)[taskIndex]?.name,
          onboarding_id: selectedOnboarding.id,
          file_count: fileUrls.length,
          file_type: 'onboarding_task'
        });
      }
    } catch (e) {
      console.error('保存失败:', e);
      console.error('错误详情:', {
        status: e.response?.status,
        data: e.response?.data,
        message: e.message
      });
      message.error('保存失败: ' + (e.response?.data?.message || e.message || '未知错误'));
    }
  };

  const openFileTaskModal = (taskIndex) => {
    // 获取当前任务的已上传文件信息
    const currentTask = toTasksArray(selectedOnboarding?.onboarding_tasks)[taskIndex];
    let existingFiles = [];
    
    if (currentTask && (currentTask.file_urls || currentTask.file_url)) {
      // 如果有已上传的文件，转换为AttachmentUpload组件需要的格式
      if (currentTask.file_urls && currentTask.file_urls.length > 0) {
        existingFiles = currentTask.file_urls.map((url, index) => ({
          uid: `existing-${index}`,
          name: `文件 ${index + 1}`,
          status: 'done',
          url: url,
          fileData: { id: url.split('/').pop() }
        }));
      } else if (currentTask.file_url) {
        existingFiles = [{
          uid: 'existing-0',
          name: '文件',
          status: 'done',
          url: currentTask.file_url,
          fileData: { id: currentTask.file_url.split('/').pop() }
        }];
      }
    }
    
    // 强制重置 modal 状态，确保每次打开都是干净的
    setFileTaskModal({ 
      visible: true, 
      taskIndex, 
      uploadedFiles: existingFiles, 
      notes: currentTask?.completion_notes || '', 
      isEdit: false 
    });
    

  };

  // 处理文件保存（不改变任务状态）
  const handleFileSaved = async (taskIndex, files) => {
    if (!selectedOnboarding || taskIndex === null || taskIndex === undefined) return;
    
    try {
      // 从文件对象中提取文件URLs
      const fileUrls = files.map(file => 
        file.url || (file.fileData ? `/api/upload/download/${file.fileData.id}` : '')
      ).filter(url => url);
      
      // 保存文件信息，但不改变任务状态
      await api.put(`/onboarding/${selectedOnboarding.id}/tasks`, {
        task_index: taskIndex,
        file_urls: fileUrls
      });
      
      // 刷新数据以显示新状态
      await refreshSelected(selectedOnboarding.id);
      message.success('文件已保存，请点击"完成任务"按钮完成任务');
    } catch (error) {
      console.error('保存文件信息失败:', error);
      message.error('保存文件信息失败');
    }
  };

  // 完成文字任务
  const completeTextTask = async (taskIndex) => {
    if (!selectedOnboarding || taskIndex === null || taskIndex === undefined) return;
    
    try {
      // 获取当前任务的已填写记录信息
      const currentTask = toTasksArray(selectedOnboarding?.onboarding_tasks)[taskIndex];
      if (!currentTask || !currentTask.completion_notes || !currentTask.completion_notes.trim()) {
        message.error('请先填写任务记录再完成任务');
        return;
      }
      
      // 完成任务
      await api.put(`/onboarding/${selectedOnboarding.id}/tasks`, {
        task_index: taskIndex,
        status: 'completed',
        completed_at: new Date().toISOString()
      });
      
      message.success('文字任务已完成！');
      
      // 刷新数据
      await refreshSelected(selectedOnboarding.id);
      
      // 生成线索跟进记录 - 文字任务完成
      if (selectedOnboarding?.lead_id) {
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'task_completed', {
          task_name: currentTask.name,
          onboarding_id: selectedOnboarding.id,
          task_type: 'text',
          completion_method: 'text_record'
        });
      }
    } catch (error) {
      console.error('完成任务失败:', error);
      message.error('完成任务失败');
    }
  };

  // 完成文件任务
  const completeFileTask = async (taskIndex) => {
    if (!selectedOnboarding || taskIndex === null || taskIndex === undefined) return;
    
    try {
      // 获取当前任务的已保存文件信息
      const currentTask = toTasksArray(selectedOnboarding?.onboarding_tasks)[taskIndex];
      if (!currentTask || !currentTask.file_urls || currentTask.file_urls.length === 0) {
        message.error('请先保存文件再完成任务');
        return;
      }
      
      // 完成任务
      await api.put(`/onboarding/${selectedOnboarding.id}/tasks`, {
        task_index: taskIndex,
        status: 'completed',
        completed_at: new Date().toISOString()
      });
      
      message.success('文件任务已完成！');
      
      // 刷新数据
      await refreshSelected(selectedOnboarding.id);
      
      // 生成线索跟进记录 - 文件任务完成
      if (selectedOnboarding?.lead_id) {
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'task_completed', {
          task_name: currentTask.name,
          onboarding_id: selectedOnboarding.id,
          task_type: 'file',
          completion_method: 'file_upload'
        });
      }
    } catch (error) {
      console.error('完成任务失败:', error);
      message.error('完成任务失败');
    }
  };

  const allTasksCompleted = toTasksArray(selectedOnboarding?.onboarding_tasks).length > 0 &&
    toTasksArray(selectedOnboarding?.onboarding_tasks).every(t => 
      t.status === 'completed' || t.status === 'COMPLETED'
    );

  const handleCreateEmployee = async () => {
    if (!selectedOnboarding) {
      message.error('入职记录信息缺失');
      return;
    }
    
    // 验证任务完成状态
    const tasks = toTasksArray(selectedOnboarding?.onboarding_tasks);
    const completedTasks = tasks.filter(t => t.status === 'completed' || t.status === 'COMPLETED');
    
    if (tasks.length === 0) {
      message.error('没有找到入职任务');
      return;
    }
    
    if (completedTasks.length !== tasks.length) {
      message.error(`还有 ${tasks.length - completedTasks.length} 个任务未完成，无法创建员工`);
      return;
    }
    
    try {
      // 工号由后端自动生成
      
      // 查找部门和职位的ID
      let departmentId = null;
      let positionId = null;
      
      try {
        // 查找部门ID
        if (selectedOnboarding.department) {
          const deptResponse = await api.get('/departments');
          const dept = deptResponse.data.find(d => d.name === selectedOnboarding.department);
          if (dept) {
            departmentId = dept.id;
          }
        }
        
        // 查找职位ID
        if (selectedOnboarding.position) {
          const posResponse = await api.get('/positions');
          const pos = posResponse.data.find(p => p.name === selectedOnboarding.position);
          if (pos) {
            positionId = pos.id;
          }
        }
      } catch (error) {
        console.warn('查找部门或职位ID失败，将使用名称字段:', error);
      }
      
      // 验证必填字段
      if (!selectedOnboarding.employee_name) {
        message.error('员工姓名不能为空');
        return;
      }
      
      if (!selectedOnboarding.start_date) {
        message.error('入职日期不能为空，请先设置入职日期');
        return;
      }
      
      // 创建员工
      const employeeData = {
        name: selectedOnboarding.employee_name,
        department_id: departmentId, // 使用ID而不是名称
        position_id: positionId,     // 使用ID而不是名称
        hire_date: dayjs(selectedOnboarding.start_date).format('YYYY-MM-DD'),
        interview_id: selectedOnboarding.interview_id || null,
        // 保留名称字段作为备用
        department_name: selectedOnboarding.department,
        position_name: selectedOnboarding.position
      };
      
      console.log('🔍 创建员工数据:', employeeData);
      
      const employeeResponse = await api.post('/employees', employeeData);
      console.log('🔍 员工创建成功:', employeeResponse.data);
      
      // 将工单置为完成
      await api.put(`/onboarding/${selectedOnboarding.id}`, { status: 'completed' });
      
      // 生成线索跟进记录 - 创建员工
      if (selectedOnboarding?.lead_id) {
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'employee_created', {
          employee_name: selectedOnboarding.employee_name,
          position: selectedOnboarding.position,
          department: selectedOnboarding.department,
          onboarding_id: selectedOnboarding.id
        });
      }
      
      message.success('已创建员工并完成入职');
      setDrawerVisible(false);
      fetchOnboarding();
    } catch (error) {
      console.error('创建员工失败:', error);
      console.error('错误详情:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // 提供更详细的错误信息
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || '数据格式错误';
        message.error(`创建员工失败：${errorMessage}，请检查员工信息`);
      } else if (error.response?.status === 409) {
        message.error('创建员工失败：员工已存在或信息冲突');
      } else if (error.response?.status === 500) {
        const errorMessage = error.response?.data?.message || '服务器内部错误';
        message.error(`创建员工失败：${errorMessage}，请稍后重试`);
        console.error('服务器错误详情:', error.response?.data);
      } else {
        const errorMessage = error.response?.data?.message || error.message || '未知错误';
        message.error(`创建员工失败：${errorMessage}`);
      }
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      pending: 'default',
      in_progress: 'processing',
      file_uploaded: 'warning',
      completed: 'success',
      cancelled: 'red'
    };
    return statusMap[status] || 'default';
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: '待开始',
      in_progress: '进行中',
      file_uploaded: '文件已上传',
      completed: '已完成',
      cancelled: '已毁单'
    };
    return statusMap[status] || status;
  };

  const getProgressPercent = (tasks) => {
    if (!tasks || !Array.isArray(tasks) || tasks.length === 0) return 0;
    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  // 开始任务
  const handleStartTask = async (onboardingId, taskIndex) => {
    try {
      await api.put(`/onboarding/${onboardingId}/tasks`, {
        task_index: taskIndex,
        status: 'in_progress',
        started_at: new Date().toISOString()
      });
      message.success('任务已开始');
      await fetchOnboarding();
      await refreshSelected(onboardingId);
      
      // 生成线索跟进记录 - 任务开始
      if (selectedOnboarding?.lead_id) {
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'task_started', {
          task_name: toTasksArray(selectedOnboarding.onboarding_tasks)[taskIndex]?.name,
          onboarding_id: onboardingId,
          task_type: toTasksArray(selectedOnboarding.onboarding_tasks)[taskIndex]?.task_type || 'unknown'
        });
      }
    } catch (error) {
      console.error('开始任务失败:', error);
      message.error('开始任务失败');
    }
  };

  // 完成任务
  const handleCompleteTask = async (onboardingId, taskIndex) => {
    try {
      await api.put(`/onboarding/${onboardingId}/tasks`, {
        task_index: taskIndex,
        status: 'completed',
        completed_at: new Date().toISOString()
      });
      message.success('任务已完成');
      await fetchOnboarding();
      await refreshSelected(onboardingId);
      
      // 生成线索跟进记录
      if (selectedOnboarding?.lead_id) {
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'task_completed', {
          task_name: toTasksArray(selectedOnboarding.onboarding_tasks)[taskIndex]?.name,
          onboarding_id: onboardingId
        });
      }
    } catch (error) {
      console.error('完成任务失败:', error);
      message.error('完成任务失败');
    }
  };

  // 删除任务
  const handleDeleteTask = async (onboardingId, taskIndex) => {
    try {
      await api.delete(`/onboarding/${onboardingId}/tasks/${taskIndex}`);
      message.success('任务已删除');
      await fetchOnboarding();
      await refreshSelected(onboardingId);
    } catch (error) {
      console.error('删除任务失败:', error);
      message.error('删除任务失败');
    }
  };

  // 获取可用任务列表
  const fetchAvailableTasks = async () => {
    if (!selectedOnboarding) return;
    
    setAddTaskModal(prev => ({ ...prev, loading: true }));
    try {
      const response = await api.get('/onboarding-tasks', {
        params: {
          department_id: selectedOnboarding.department_id,
          position_id: selectedOnboarding.position_id
        }
      });
      
      // 获取当前已有的任务名称
      const currentTasks = toTasksArray(selectedOnboarding.onboarding_tasks);
      const currentTaskNames = currentTasks.map(task => task.name);
      
      // 过滤掉已经存在的任务和已删除的任务
      const availableTasks = response.data.filter(task => 
        !currentTaskNames.includes(task.name) && 
        !task.deleted_at &&
        task.name && task.name.trim() !== ''
      );
      
      setAddTaskModal(prev => ({ 
        ...prev, 
        availableTasks,
        selectedTasks: [],
        loading: false 
      }));
    } catch (error) {
      console.error('获取可用任务失败:', error);
      message.error('获取可用任务失败');
      setAddTaskModal(prev => ({ ...prev, loading: false }));
    }
  };

  // 添加任务到入职流程
  const handleAddTask = async () => {
    if (!addTaskModal.selectedTasks || addTaskModal.selectedTasks.length === 0 || !selectedOnboarding) {
      message.warning('请选择要添加的任务');
      return;
    }
    
    try {
      // 批量添加任务
      for (const task of addTaskModal.selectedTasks) {
        await api.put(`/onboarding/${selectedOnboarding.id}/tasks`, {
          task_data: task
        });
      }
      
      // 更新本地状态
      await fetchOnboarding();
      await refreshSelected(selectedOnboarding.id);
      
      // 生成跟进记录
      if (selectedOnboarding.lead_id) {
        const taskNames = addTaskModal.selectedTasks.map(task => task.name).join('、');
        await generateFollowUpRecord(selectedOnboarding.lead_id, 'tasks_added', {
          task_names: taskNames,
          task_count: addTaskModal.selectedTasks.length,
          onboarding_id: selectedOnboarding.id
        });
      }
      
      message.success(`成功添加 ${addTaskModal.selectedTasks.length} 个任务`);
      setAddTaskModal({ visible: false, availableTasks: [], selectedTasks: [], loading: false });
    } catch (error) {
      console.error('添加任务失败:', error);
      message.error('添加任务失败');
    }
  };

  // 生成线索跟进记录
  const generateFollowUpRecord = async (leadId, type, data) => {
    try {
      let content = '';
      switch (type) {
        case 'task_started':
          content = `开始执行入职任务：${data.task_name}`;
          break;
        case 'task_completed':
          content = `完成入职任务：${data.task_name}`;
          break;
        case 'onboarding_created':
          content = `创建入职工单，员工：${data.employee_name}，职位：${data.position}`;
          break;
        case 'onboarding_status_changed':
          content = `入职状态变更为：${data.new_status}`;
          break;
        case 'task_added':
          content = `管理员添加了入职任务：${data.task_name}`;
          break;
        case 'tasks_added':
          content = `管理员批量添加了 ${data.task_count} 个入职任务：${data.task_names}`;
          break;
        default:
          content = '入职流程操作';
      }
      
      await api.post('/follow-ups', {
        lead_id: leadId,
        content: content,
        type: 'onboarding',
        created_by: user.id
      });
    } catch (error) {
      console.error('生成跟进记录失败:', error);
    }
  };

  // 显示任务记录
  const showTaskRecord = (task) => {
    let title = `${task.name}`;
    let content = null;
    
    // 添加调试信息
    console.log('🔍 showTaskRecord 任务对象:', task);
    console.log('🔍 任务状态:', task.status);
    console.log('🔍 文件字段检查:', {
      file_url: task.file_url,
      file_urls: task.file_urls,
      hasFileUrl: !!task.file_url,
      hasFileUrls: !!(task.file_urls && task.file_urls.length > 0)
    });
    
    if (task.status === 'completed') {
      title += ' - 完成记录';
      content = (
        <div>
          <p><strong>完成时间：</strong>{task.completed_at ? dayjs(task.completed_at).format('YYYY-MM-DD HH:mm') : '未知'}</p>
          {task.completion_notes && (
            <>
              <p><strong>完成记录：</strong></p>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}>
                {task.completion_notes}
              </div>
            </>
          )}
          {/* 改进文件显示逻辑，支持更多字段格式 */}
          {(task.file_url || (task.file_urls && task.file_urls.length > 0) || task.uploaded_files) && (
            <>
              <p><strong>上传文件：</strong></p>
              {task.file_urls && task.file_urls.length > 0 ? (
                task.file_urls.map((url, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <Button 
                      type="link" 
                      icon={<FileTextOutlined />}
                      onClick={() => window.open(url, '_blank')}
                    >
                      文件 {index + 1}
                    </Button>
                  </div>
                ))
              ) : task.file_url ? (
                <Button 
                  type="link" 
                  icon={<FileTextOutlined />}
                  onClick={() => window.open(task.file_url, '_blank')}
                >
                  查看文件
                </Button>
              ) : task.uploaded_files && task.uploaded_files.length > 0 ? (
                task.uploaded_files.map((file, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <Button 
                      type="link" 
                      icon={<FileTextOutlined />}
                      onClick={() => {
                        const fileUrl = file.url || (file.fileData ? `/api/upload/download/${file.fileData.id}` : '');
                        if (fileUrl) {
                          window.open(fileUrl, '_blank');
                        }
                      }}
                    >
                      {file.name || `文件 ${index + 1}`}
                    </Button>
                  </div>
                ))
              ) : null}
            </>
          )}
        </div>
      );
    } else {
      title += ' - 任务信息';
      content = (
        <div>
          <p><strong>任务描述：</strong>{task.description || '无描述'}</p>
          <p><strong>任务类型：</strong>{task.task_type === 'file' ? '文件任务' : '文字任务'}</p>
          <p><strong>当前状态：</strong>
            <Tag color={getTaskStatusColor(task.status)}>
              {getTaskStatusText(task.status)}
            </Tag>
          </p>
          {task.started_at && (
            <p><strong>开始时间：</strong>{dayjs(task.started_at).format('YYYY-MM-DD HH:mm')}</p>
          )}
        </div>
      );
    }
    
    Modal.info({
      title: title,
      content: content,
      width: 600,
      okText: '关闭'
    });
  };

  // 显示文件内容弹窗
  const showFileModal = (task) => {
    // 获取文件URLs，支持单个文件或多个文件
    const fileUrls = task.file_urls || (task.file_url ? [task.file_url] : []);
    
    if (fileUrls.length === 0) {
      Modal.info({
        title: `${task.name} - 文件内容`,
        content: <div style={{ textAlign: 'center', padding: '20px' }}>暂无文件</div>,
        width: 600,
        okText: '关闭'
      });
      return;
    }
    
    // 如果只有一个文件，使用原来的逻辑
    if (fileUrls.length === 1) {
      showSingleFileModal(task, fileUrls[0]);
      return;
    }
    
    // 多个文件：显示文件列表
    const content = (
      <div>
        <div style={{ marginBottom: '16px' }}>
          <Text strong>共上传了 {fileUrls.length} 个文件：</Text>
        </div>
        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
          {fileUrls.map((fileUrl, index) => {
            const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileUrl);
            const isPdf = /\.pdf$/i.test(fileUrl);
            const fileName = fileUrl.split('/').pop();
            
            return (
              <div key={index} style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                border: '1px solid #d9d9d9', 
                borderRadius: '4px',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Text strong>文件 {index + 1}: {fileName}</Text>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => showSingleFileModal(task, fileUrl, index + 1)}
                  >
                    查看详情
                  </Button>
                </div>
                
                {/* 图片预览 */}
                {isImage && (
                  <div style={{ textAlign: 'center' }}>
                    <img 
                      src={fileUrl} 
                      alt={`${task.name} - 文件${index + 1}`}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px'
                      }} 
                    />
                  </div>
                )}
                
                {/* 文件类型标识 */}
                <div style={{ marginTop: '8px', textAlign: 'center' }}>
                  <Tag color={isImage ? 'green' : isPdf ? 'blue' : 'default'}>
                    {isImage ? '图片' : isPdf ? 'PDF' : '文件'}
                  </Tag>
                  <Button
                    type="link"
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={() => window.open(fileUrl, '_blank')}
                    style={{ marginLeft: '8px' }}
                  >
                    在新窗口查看
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
    
    Modal.info({
      title: `${task.name} - 文件列表 (${fileUrls.length}个文件)`,
      content: content,
      width: 800,
      okText: '关闭'
    });
  };
  
  // 显示单个文件的模态框
  const showSingleFileModal = (task, fileUrl, fileIndex = 1) => {
    // 判断文件类型
    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileUrl);
    const isPdf = /\.pdf$/i.test(fileUrl);
    const isText = /\.(txt|md|log)$/i.test(fileUrl);
    
    let content = null;
    
    if (isImage) {
      // 图片文件：显示图片预览
      content = (
        <div style={{ textAlign: 'center' }}>
          <img 
            src={fileUrl} 
            alt={task.name}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '400px', 
              objectFit: 'contain',
              border: '1px solid #d9d9d9',
              borderRadius: '4px'
            }} 
          />
          <div style={{ marginTop: '16px' }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => window.open(fileUrl, '_blank')}
            >
              在新窗口查看
            </Button>
          </div>
        </div>
      );
    } else if (isPdf) {
      // PDF文件：显示PDF预览链接
      content = (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <FileTextOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
          </div>
          <Text strong>PDF文件</Text>
          <div style={{ marginTop: '16px' }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => window.open(fileUrl, '_blank')}
            >
              查看PDF文件
            </Button>
          </div>
        </div>
      );
    } else if (isText) {
      // 文本文件：显示文本内容
      content = (
        <div>
          <Text type="secondary">文件内容：</Text>
          <div style={{ 
            marginTop: '8px',
            padding: '12px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
            maxHeight: '300px',
            overflow: 'auto'
          }}>
            <pre style={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-all', 
              fontSize: '14px', 
              lineHeight: '1.6',
              margin: 0
            }}>
              {task.completion_notes || '暂无文件内容'}
            </pre>
          </div>
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => window.open(fileUrl, '_blank')}
            >
              下载文件
            </Button>
          </div>
        </div>
      );
    } else {
      // 其他类型文件：显示下载链接
      content = (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <FileTextOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
          </div>
          <Text strong>文件：{fileUrl.split('/').pop()}</Text>
          <div style={{ marginTop: '16px' }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => window.open(fileUrl, '_blank')}
            >
              下载文件
            </Button>
          </div>
        </div>
      );
    }
    
    const title = fileIndex > 1 ? `${task.name} - 文件${fileIndex}` : `${task.name} - 文件内容`;
    
    Modal.info({
      title: title,
      content: content,
      width: 600,
      okText: '关闭'
    });
  };

  // 获取任务状态颜色
  const getTaskStatusColor = (status) => {
    const statusMap = {
      pending: '#d9d9d9',      // 灰色
      in_progress: '#1890ff',  // 蓝色
      completed: '#52c41a',    // 绿色
      cancelled: '#ff4d4f'     // 红色
    };
    return statusMap[status] || '#d9d9d9';
  };

  // 获取任务状态图标
  const getTaskStatusIcon = (status) => {
    const statusMap = {
      pending: '○',           // 圆圈
      in_progress: '▶',       // 播放
      file_uploaded: '📁',    // 文件夹
      completed: '✓',         // 对勾
      cancelled: '✗'          // 叉号
    };
    return statusMap[status] || '○';
  };

  // 获取任务状态文本
  const getTaskStatusText = (status) => {
    const statusMap = {
      pending: '待开始',
      in_progress: '进行中',
      completed: '已完成',
      cancelled: '已取消'
    };
    return statusMap[status] || status;
  };

  const columns = [
    {
      title: '员工姓名',
      dataIndex: 'employee_name',
      key: 'employee_name',
      render: (text, record) => (
        <div>
          <span style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleView(record)}>
            {text}
          </span>
          {record.lead_id && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
              线索ID: {record.lead_id}
            </div>
          )}
          {record.lead_source_name && (
            <div style={{ fontSize: '11px', color: '#52c41a', marginTop: '2px' }}>
              📍 来源: {record.lead_source_name}
            </div>
          )}
        </div>
      )
    },
    {
      title: '职位',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: '入职日期',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        // 如果是毁单状态，显示鼠标悬停提示
        if (status === 'cancelled' && record.notes) {
          return (
            <Tooltip title={`毁单理由：${record.notes}`} placement="top">
              <Tag color={getStatusColor(status)}>
                {getStatusText(status)}
              </Tag>
            </Tooltip>
          );
        }

        return (
          <Tag color={getStatusColor(status)}>
            {getStatusText(status)}
          </Tag>
        );
      }
    },
    {
      title: '进度',
      key: 'progress',
      render: (_, record) => {
        const percent = getProgressPercent(toTasksArray(record.onboarding_tasks));
        return <Progress percent={percent} size="small" />;
      }
    },
    {
      title: '创建人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      render: (text, record) => {
        return record.created_by_name || record.created_by_username || '-';
      }
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="查看详情"
          />
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="编辑"
          />
        </Space>
      )
    }
  ];

  const handleFilterChange = (key, value) => {
    console.log('🔍 筛选条件变化:', key, value);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      search: '',
      status: '',
      department_id: '',
      view_mode: 'all',
      onboarding_id: '',
      dateRange: null
    });
  };

  // 计算统计数据
  console.log('🚀 开始计算统计数据, onboarding数组:', onboarding);
  console.log('🚀 onboarding是否为数组:', Array.isArray(onboarding));
  console.log('🚀 onboarding长度:', onboarding?.length);

  // 使用全局统计数据，而不是当前页面数据
  const [globalStats, setGlobalStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    overdue: 0
  });

  // 概览统计数据
  const [overviewStats, setOverviewStats] = useState({
    monthly: {
      total: 0,
      completed: 0,
      cancelled: 0,
      onboardingRate: '0.0'
    },
    weekly: {
      total: 0,
      completed: 0,
      cancelled: 0,
      onboardingRate: '0.0'
    }
  });

  // 根据筛选条件计算统计数据（仅在时间筛选时使用）
  const calculateFilteredStats = () => {
    if (!Array.isArray(onboarding)) {
      return globalStats;
    }

    // 只有在时间筛选时才根据筛选条件计算统计
    if (!filters.dateRange || filters.dateRange.length !== 2) {
      return globalStats;
    }

    // 根据筛选条件过滤数据
    let filteredData = [...onboarding];
    
    // 应用搜索筛选
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredData = filteredData.filter(item => 
        item.employee_name?.toLowerCase().includes(searchLower) ||
        item.phone?.includes(searchLower) ||
        item.employee_no?.toLowerCase().includes(searchLower)
      );
    }
    
    // 应用状态筛选（除了overdue，因为overdue是计算出来的）
    if (filters.status && filters.status !== 'overdue') {
      filteredData = filteredData.filter(item => item.status === filters.status);
    }
    
    // 应用部门筛选
    if (filters.department_id) {
      filteredData = filteredData.filter(item => item.department_id == filters.department_id);
    }
    
    // 应用入职ID筛选
    if (filters.onboarding_id) {
      filteredData = filteredData.filter(item => item.id == filters.onboarding_id);
    }
    
    // 应用日期范围筛选
    const [startDate, endDate] = filters.dateRange;
    filteredData = filteredData.filter(item => {
      if (!item.created_at) return false;
      const itemDate = dayjs(item.created_at);
      return itemDate.isAfter(startDate) && itemDate.isBefore(endDate);
    });

    // 计算各状态数量
    const stats = {
      total: filteredData.length,
      pending: filteredData.filter(item => item.status === 'pending').length,
      inProgress: filteredData.filter(item => item.status === 'in_progress').length,
      completed: filteredData.filter(item => item.status === 'completed').length,
      cancelled: filteredData.filter(item => item.status === 'cancelled').length,
      overdue: 0
    };

    // 计算超期数量（已完成和已取消的不算超期）
    if (filters.status === 'overdue' || !filters.status) {
      stats.overdue = filteredData.filter(item => {
        if (item.status === 'completed' || item.status === 'cancelled') return false;
        if (!item.start_date) return false;
        const startDate = item.start_date;
        const today = new Date().toISOString().split('T')[0];
        return startDate <= today;
      }).length;
    }

    return stats;
  };

  const stats = calculateFilteredStats();
  
  console.log('📊 根据筛选条件计算的统计数据:', stats);
  console.log('📊 当前筛选条件:', filters);
  console.log('📊 当前数据长度:', onboarding?.length);


  return (
    <ConfigProvider locale={zhCN}>
      <style>
        {`
          .ant-table-tbody > tr.overdue-row > td {
            background-color: #fff2f0 !important;
            border-left: 4px solid #ff4d4f !important;
          }
          .ant-table-tbody > tr.overdue-row:hover > td {
            background-color: #ffe7e6 !important;
          }
          .ant-table-tbody > tr.highlighted-row > td {
            background-color: #e6f7ff !important;
          }
        `}
      </style>
      <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0 }}>入职管理</h1>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加入职流程
          </Button>
        </div>

        {/* 概览统计卡片 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col span={12}>
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff', marginBottom: '4px' }}>
                  本月概览
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  总入职：<span style={{ color: '#1890ff', fontWeight: 'bold' }}>{overviewStats.monthly.total}</span>人 | 
                  实际入职：<span style={{ color: '#52c41a', fontWeight: 'bold' }}>{overviewStats.monthly.completed}</span>人 | 
                  爽约：<span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{overviewStats.monthly.cancelled}</span>人 | 
                  入职率：<span style={{ color: '#722ed1', fontWeight: 'bold' }}>{overviewStats.monthly.onboardingRate}%</span>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a', marginBottom: '4px' }}>
                  本周概览
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  总入职：<span style={{ color: '#1890ff', fontWeight: 'bold' }}>{overviewStats.weekly.total}</span>人 | 
                  实际入职：<span style={{ color: '#52c41a', fontWeight: 'bold' }}>{overviewStats.weekly.completed}</span>人 | 
                  爽约：<span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{overviewStats.weekly.cancelled}</span>人 | 
                  入职率：<span style={{ color: '#722ed1', fontWeight: 'bold' }}>{overviewStats.weekly.onboardingRate}%</span>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="总入职流程"
                value={stats.total}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="待开始"
                value={stats.pending}
                valueStyle={{ color: '#faad14' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="进行中"
                value={stats.inProgress}
                valueStyle={{ color: '#1890ff' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="已完成"
                value={stats.completed}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="已毁单"
                value={stats.cancelled}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="已超期"
                value={stats.overdue}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<AlertOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* 筛选器 */}
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>筛选</span>
              {isAdmin() && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ whiteSpace: 'nowrap', color: '#555' }}>查看模式：</div>
                  <Radio.Group
                    value={filters.view_mode}
                    onChange={(e) => handleFilterChange('view_mode', e.target.value)}
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="my">只看我的</Radio.Button>
                    <Radio.Button value="all">查看所有</Radio.Button>
                  </Radio.Group>
                </div>
              )}
            </div>
          }
        >
          <Row gutter={16}>
            <Col span={20}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ width: '12.5%', minWidth: 75 }}>
                  <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>员工姓名</div>
                  <Input
                    placeholder="搜索员工姓名"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    allowClear
                  />
                </div>
                <div style={{ minWidth: 120 }}>
                  <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>状态</div>
                  <Select
                    placeholder="选择状态"
                    value={filters.status}
                    onChange={(value) => handleFilterChange('status', value)}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value="pending">待开始</Option>
                    <Option value="in_progress">进行中</Option>
                    <Option value="completed">已完成</Option>
                    <Option value="cancelled">已毁单</Option>
                    <Option value="overdue">已过期</Option>
                  </Select>
                </div>
                <div style={{ minWidth: 320 }}>
                  <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>部门</div>
                  <DepartmentSelect
                    placeholder="选择部门"
                    value={filters.department_id}
                    onChange={(value) => handleFilterChange('department_id', value)}
                    allowClear
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>入职记录ID</div>
                  <Input
                    placeholder="入职记录ID"
                    value={filters.onboarding_id}
                    onChange={(e) => handleFilterChange('onboarding_id', e.target.value)}
                    allowClear
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ minWidth: 240 }}>
                  <div style={{ marginBottom: 4, fontSize: '12px', color: '#666' }}>入职时间范围</div>
                  <DatePicker.RangePicker
                    placeholder={['开始日期', '结束日期']}
                    value={filters.dateRange}
                    onChange={(dates) => handleFilterChange('dateRange', dates)}
                    allowClear
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    locale={{
                      ...zhCN.DatePicker,
                      locale: {
                        ...zhCN.DatePicker.locale,
                        monthFormat: 'M',
                        shortMonths: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']
                      }
                    }}
                  />
                </div>
              </div>
            </Col>
            
            {/* 右侧按钮组 */}
            <Col span={4}>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button 
                  onClick={handleClearAllFilters} 
                  style={{ whiteSpace: 'nowrap' }}
                  title="清除所有筛选条件"
                >
                  清除筛选
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchOnboarding}
                  loading={loading}
                  title="刷新数据"
                >
                  刷新
                </Button>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 数据表格 */}
                  <Table
                    columns={columns}
                    dataSource={Array.isArray(onboarding) ? onboarding : []}
                    rowKey="id"
                    loading={loading}
                    rowClassName={(record) => {
                      let className = '';
                      
                      console.log(`🎨 处理行样式 ID:${record.id}, 状态:${record.status}, 日期:${record.start_date}`);
                      
                      // 高亮显示特定行
                      if (highlightedOnboardingId && record.id === highlightedOnboardingId) {
                        className += 'highlighted-row ';
                        console.log(`✨ 添加高亮样式 ID:${record.id}`);
                      }
                      
                      // 超期工单红色背景 - 使用简单逻辑
                      if (record.status !== 'completed' && record.status !== 'cancelled' && record.start_date) {
                        const startDate = record.start_date; // "2024-02-01"
                        const today = new Date().toISOString().split('T')[0]; // "2025-09-16"
                        const isOverdue = startDate <= today;
                        
                        console.log(`🔍 行样式超期判断 ID:${record.id} - ${startDate} <= ${today} = ${isOverdue}`);
                        
                        if (isOverdue) {
                          className += 'overdue-row ';
                          console.log(`🚨 应用超期样式 ID:${record.id}, 最终className: "${className.trim()}"`);
                        }
                      }
                      
                      const finalClassName = className.trim();
                      console.log(`🎯 最终行样式 ID:${record.id}: "${finalClassName}"`);
                      return finalClassName;
                    }}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
                    }}
                    onChange={handleTableChange}
                  />
                </Card>

      <style jsx>{`
        .highlighted-row {
          background-color: #fff7e6 !important;
          border-left: 4px solid #fa8c16 !important;
        }
        .highlighted-row:hover {
          background-color: #fff2e8 !important;
        }
      `}</style>

      {/* 添加/编辑模态框 */}
      <Modal
        title={editingOnboarding ? '编辑入职流程' : '添加入职流程'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="employee_name"
                label="员工姓名"
                rules={[{ required: true, message: '请输入员工姓名' }]}
              >
                <Input placeholder="请输入员工姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="入职日期"
                rules={[{ required: true, message: '请选择入职日期' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="请选择入职日期" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="手机号"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式' }
                ]}
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="邮箱"
                rules={[
                  { type: 'email', message: '请输入正确的邮箱格式' }
                ]}
              >
                <Input placeholder="请输入邮箱（可选）" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="department_id"
                label="部门"
                rules={[{ required: true, message: '请选择部门' }]}
              >
                <DepartmentSelect
                  placeholder="请选择部门"
                  onChange={(value) => {
                    setSelectedDepartmentId(value);
                    // 当部门改变时，清空职位选择
                    form.setFieldsValue({ position_id: undefined });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="position_id"
                label="职位"
                rules={[{ required: true, message: '请选择职位' }]}
              >
                <PositionSelect
                  placeholder="请选择职位"
                  departmentId={selectedDepartmentId}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editingOnboarding ? '更新' : '创建'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情抽屉 */}
      <Drawer
        title="入职流程详情"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={820}
      >
        {selectedOnboarding && (
          <div>
            {/* 基本信息卡片 */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>员工姓名：</Text>
                  <Text>{selectedOnboarding.employee_name}</Text>
                  {selectedOnboarding.lead_id && (
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      (线索ID: {selectedOnboarding.lead_id})
                    </Text>
                  )}
                </Col>
                <Col span={12}>
                  <Text strong>职位：</Text>
                  <Text>{selectedOnboarding.position}</Text>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text strong>部门：</Text>
                  <Text>{selectedOnboarding.department}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>入职日期：</Text>
                  <Text>{selectedOnboarding.start_date ? dayjs(selectedOnboarding.start_date).format('YYYY-MM-DD') : '-'}</Text>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text strong>手机号：</Text>
                  <Text>{selectedOnboarding.lead_phone || selectedOnboarding.phone || '-'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>添加时间：</Text>
                  <Text>{selectedOnboarding.created_at ? dayjs(selectedOnboarding.created_at).format('YYYY-MM-DD HH:mm') : '-'}</Text>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text strong>线索来源：</Text>
                  {selectedOnboarding.lead_source_name ? (
                    <Tag color={selectedOnboarding.lead_source_color || '#1890ff'}>
                      {selectedOnboarding.lead_source_name}
                    </Tag>
                  ) : (
                    <Text>-</Text>
                  )}
                </Col>
                <Col span={12}>
                  <Text strong>面试人：</Text>
                  <Text>{selectedOnboarding.interview_interviewer || '-'}</Text>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text strong>状态：</Text>
                  {selectedOnboarding.status === 'cancelled' && selectedOnboarding.notes ? (
                    <Tooltip title={`毁单理由：${selectedOnboarding.notes}`} placement="top">
                      <Tag color={getStatusColor(selectedOnboarding.status)}>
                        {getStatusText(selectedOnboarding.status)}
                      </Tag>
                    </Tooltip>
                  ) : (
                    <Tag color={getStatusColor(selectedOnboarding.status)}>
                      {getStatusText(selectedOnboarding.status)}
                    </Tag>
                  )}
                </Col>
                <Col span={12}>
                  <Text strong>进度：</Text>
                  <Progress
                    percent={getProgressPercent(toTasksArray(selectedOnboarding?.onboarding_tasks))}
                    size="small"
                    style={{ display: 'inline-block', width: 100 }}
                  />
                </Col>
              </Row>
            </Card>

            {/* 到店状态确认卡片 - 只在待开始状态时显示 */}
            {selectedOnboarding.status === 'pending' && (
              <Card 
                title="到店状态确认" 
                size="small" 
                style={{ marginBottom: 16 }}
                extra={
                  <Tag color="processing">待确认到店</Tag>
                }
              >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                    请确认员工是否已到店，确认后将开始入职流程
                  </Text>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button
                      type="primary"
                      size="large"
                      icon={<CheckCircleOutlined />}
                      onClick={() => setConfirmArrivalModal({ visible: true })}
                    >
                      确认已到店
                    </Button>
                    <Button
                      danger
                      size="large"
                      icon={<CloseCircleOutlined />}
                      onClick={() => setStatusModal({ visible: true, targetStatus: 'cancelled', notes: '' })}
                    >
                      毁单
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* 入职流程操作卡片 - 只在有未完成任务时显示 */}
            {selectedOnboarding.status === 'in_progress' && !allTasksCompleted && (
              <Card 
                title="入职流程操作" 
                size="small" 
                style={{ marginBottom: 16 }}
                extra={
                  <Tag color="processing">入职中</Tag>
                }
              >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                    员工已到店，正在进行入职流程
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    请完成所有入职任务
                  </Text>
                </div>
              </Card>
            )}

            {/* 全部完成提示卡片 - 只在所有任务完成时显示 */}
            {selectedOnboarding.status === 'in_progress' && allTasksCompleted && (
              <Card 
                title="入职流程完成" 
                size="small" 
                style={{ marginBottom: 16 }}
                extra={
                  <Tag color="success">已完成</Tag>
                }
              >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                    所有入职任务已完成
                  </Text>
                  <Button 
                    type="primary" 
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={handleCreateEmployee}
                  >
                    创建正式员工
                  </Button>
                </div>
              </Card>
            )}
            

            
            <Card 
              title="入职任务" 
              size="small"
              extra={
                <PermissionGuard permission="onboarding.edit">
                  {selectedOnboarding.status !== 'completed' && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setAddTaskModal({ visible: true, availableTasks: [], selectedTasks: [], loading: false });
                        fetchAvailableTasks();
                      }}
                    >
                      新增任务
                    </Button>
                  )}
                </PermissionGuard>
              }
            >
              {/* 任务状态流转说明 */}
              <div style={{ 
                marginBottom: '16px', 
                padding: '12px', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '4px', 
                border: '1px solid #bae7ff',
                fontSize: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ marginRight: '8px' }}>📋</span>
                  <Text strong>任务状态流转说明：</Text>
                </div>
                <div style={{ color: '#666', lineHeight: '1.6' }}>
                  <div>• <Text strong>待开始</Text>：任务尚未开始，点击"开始任务"按钮开始执行</div>
                  <div>• <Text strong>进行中</Text>：任务正在进行，填写内容或上传文件后可以完成任务</div>
                  <div>• <Text strong>已完成</Text>：任务已完成，可以查看文件或记录</div>
                </div>
              </div>
              
              <List
                dataSource={toTasksArray(selectedOnboarding?.onboarding_tasks)}
                renderItem={(task, index) => (
                  <List.Item
                    actions={[
                      // 根据任务状态显示不同的操作按钮
                      <Space key="task-actions">
                        {/* 待开始状态：显示开始任务按钮 */}
                        {(task.status === 'pending' || !task.status || task.status === '') && (
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleStartTask(selectedOnboarding.id, index)}
                            disabled={selectedOnboarding.status !== 'in_progress'}
                          >
                            开始任务
                          </Button>
                        )}
                        
                        {/* 进行中状态：显示操作按钮 */}
                        {task.status === 'in_progress' && (
                          <>
                            {/* 根据任务名称或类型判断任务类型 */}
                            {(task.task_type === 'text' || task.task_type === 'TEXT' || 
                              (task.name && (task.name.includes('介绍') || task.name.includes('培训') || task.name.includes('熟悉')))) ? (
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => openTextTaskModal(index)}
                              >
                                填写记录
                              </Button>
                            ) : (
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => openFileTaskModal(index)}
                              >
                                上传文件
                              </Button>
                            )}
                          </>
                        )}
                        
                        {/* 文字任务已填写记录状态：显示完成任务按钮 */}
                        {(task.status === 'in_progress' && task.completion_notes && task.completion_notes.trim()) && (
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => completeTextTask(index)}
                          >
                            完成任务
                          </Button>
                        )}
                        
                        {/* 文件任务已保存状态：显示完成任务按钮 */}
                        {(task.status === 'in_progress' && task.file_urls && task.file_urls.length > 0) && (
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => completeFileTask(index)}
                          >
                            完成任务
                          </Button>
                        )}
                        
                        {/* 文件已保存状态：显示完成任务按钮 */}
                        {(task.status === 'in_progress' && task.file_urls && task.file_urls.length > 0) && (
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => completeFileTask(index)}
                          >
                            完成任务
                          </Button>
                        )}
                        

                        
                        {/* 已完成状态：显示查看按钮 */}
                        {task.status === 'completed' && (
                          <>
                            {/* 文件任务：显示查看文件按钮 */}
                            {(task.file_urls && task.file_urls.length > 0) || task.file_url ? (
                              <Button
                                type="link"
                                size="small"
                                icon={<FileTextOutlined />}
                                onClick={() => showFileModal(task)}
                              >
                                查看文件{task.file_urls && task.file_urls.length > 1 ? `(${task.file_urls.length})` : ''}
                              </Button>
                            ) : null}
                            {/* 文字任务或文件任务有备注：显示查看记录按钮 */}
                            {task.completion_notes && (
                              <Button
                                type="link"
                                size="small"
                                icon={<FileTextOutlined />}
                                onClick={() => showTaskRecord(task)}
                              >
                                查看记录
                              </Button>
                            )}
                            {/* 如果既没有文件也没有记录，显示查看任务信息 */}
                            {!task.file_url && !task.file_urls && !task.completion_notes && (
                              <Button
                                type="link"
                                size="small"
                                icon={<InfoCircleOutlined />}
                                onClick={() => showTaskRecord(task)}
                              >
                                查看任务
                              </Button>
                            )}
                          </>
                        )}
                      </Space>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <div style={{ 
                          width: 32, 
                          height: 32, 
                          borderRadius: '50%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          backgroundColor: getTaskStatusColor(task.status || 'pending'),
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          {getTaskStatusIcon(task.status || 'pending')}
                        </div>
                      }
                      title={
                        <Space size="small">
                          <Tag color={getTaskStatusColor(task.status || 'pending')}>
                            {getTaskStatusText(task.status || 'pending')}
                          </Tag>
                          <Tag color={(task.task_type === 'file' || task.task_type === 'FILE' || 
                            (task.name && !task.name.includes('介绍') && !task.name.includes('培训') && !task.name.includes('熟悉'))) ? 'blue' : 'green'}>
                            {(task.task_type === 'file' || task.task_type === 'FILE' || 
                              (task.name && !task.name.includes('介绍') && !task.name.includes('培训') && !task.name.includes('熟悉'))) ? '文件任务' : '文字任务'}
                          </Tag>
                          <Text strong>{task.name}</Text>
                          <PermissionGuard permission="onboarding.edit">
                            {selectedOnboarding.status !== 'completed' && (
                              <Popconfirm
                                title="确定要删除这个任务吗？"
                                description="删除后无法恢复，且会在线索跟进记录中生成删除记录。"
                                onConfirm={() => handleDeleteTask(selectedOnboarding.id, index)}
                                okText="确定删除"
                                cancelText="取消"
                              >
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  title="删除任务"
                                  style={{ marginLeft: 8 }}
                                />
                              </Popconfirm>
                            )}
                          </PermissionGuard>
                        </Space>
                      }
                      description={
                        <div>
                          <div style={{ marginBottom: '8px' }}>{task.description}</div>
                          {/* 显示任务进度时间线 */}
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {(task.status === 'completed' || task.status === 'COMPLETED') && (
                              <span>✅ 已完成 - {task.completed_at ? dayjs(task.completed_at).format('YYYY-MM-DD HH:mm') : '未知时间'}</span>
                            )}
                            {/* 文件任务已保存状态 */}
                            {(task.status === 'in_progress' && task.file_urls && task.file_urls.length > 0) && (
                              <span>📁 文件已保存 - 请点击"完成任务"按钮完成任务</span>
                            )}
                            {/* 文字任务已填写记录状态 */}
                            {(task.status === 'in_progress' && task.completion_notes && task.completion_notes.trim()) && (
                              <span>📝 记录已填写 - 请点击"完成任务"按钮完成任务</span>
                            )}
                            {/* 普通进行中状态 */}
                            {(task.status === 'in_progress' || task.status === 'IN_PROGRESS') && 
                              !(task.file_urls && task.file_urls.length > 0) && 
                              !(task.completion_notes && task.completion_notes.trim()) && (
                              <span>🔄 进行中 - {task.started_at ? dayjs(task.started_at).format('YYYY-MM-DD HH:mm') : '未知时间'}</span>
                            )}
                            {(task.status === 'pending' || task.status === 'PENDING' || !task.status || task.status === '') && (
                              <span>⏳ 待开始</span>
                            )}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </div>
        )}
      </Drawer>

      {/* 确认到店弹窗 */}
      <Modal
        title="确认到店"
        open={confirmArrivalModal.visible}
        onCancel={() => setConfirmArrivalModal({ visible: false })}
        onOk={async () => {
          try {
            await handleUpdateStatus('in_progress', '');
            setConfirmArrivalModal({ visible: false });
            // 刷新抽屉内容，确保任务列表正确显示
            if (selectedOnboarding) {
              await refreshSelected(selectedOnboarding.id);
            }
          } catch (error) {
            console.error('确认到店失败:', error);
          }
        }}
        okText="确认"
        cancelText="取消"
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <WarningOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
            确认入职用户已到店
          </div>
          <div style={{ color: '#ff4d4f', fontSize: '14px' }}>
            此状态不可逆！
          </div>
        </div>
      </Modal>

      {/* 入职状态变更备注弹窗 */}
      <Modal
        title={statusModal.targetStatus === 'in_progress' ? '改为入职中' : '毁单'}
        open={statusModal.visible}
        onCancel={() => setStatusModal({ visible: false, targetStatus: '', notes: '' })}
        onOk={() => handleUpdateStatus(statusModal.targetStatus, statusModal.notes)}
      >
        <Input.TextArea
          placeholder={statusModal.targetStatus === 'cancelled' ? "填写毁单理由（必填）" : "填写备注（可选）"}
          value={statusModal.notes}
          onChange={(e) => setStatusModal(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
        />
      </Modal>

      {/* 文字任务备注弹窗 */}
      <Modal
        title={textTaskModal.isEdit ? "编辑任务记录" : "填写任务记录"}
        open={textTaskModal.visible}
        onCancel={() => setTextTaskModal({ visible: false, taskIndex: null, notes: '', isEdit: false })}
        footer={[
          <Button key="cancel" onClick={() => setTextTaskModal({ visible: false, taskIndex: null, notes: '', isEdit: false })}>
            取消
          </Button>,
                      <Button key="save" type="primary" onClick={saveTextTaskDraft}>
              保存记录
            </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            {textTaskModal.isEdit 
              ? '请修改此任务的执行记录，修改完成后点击"保存记录"即可更新记录'
              : '请填写此任务的执行记录，填写完成后点击"保存记录"即可保存记录'
            }
          </Text>
        </div>
        
        <Form layout="vertical">
          <Form.Item
            label="任务名称"
            style={{ marginBottom: '16px' }}
          >
            <Text strong>
              {selectedOnboarding && 
               toTasksArray(selectedOnboarding?.onboarding_tasks)[textTaskModal.taskIndex]?.name}
            </Text>
          </Form.Item>
          
          <Form.Item
            label="执行记录"
            required
            style={{ marginBottom: '16px' }}
          >
            <Input.TextArea
              placeholder="请详细描述任务的执行情况，例如：完成了什么、如何完成的、有什么注意事项等"
              value={textTaskModal.notes}
              onChange={(e) => setTextTaskModal(prev => ({ ...prev, notes: e.target.value }))}
              rows={6}
              showCount
              maxLength={500}
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 提示：{textTaskModal.isEdit 
                ? '修改完成后点击"保存记录"即可更新记录'
                : '填写完成后点击"保存记录"即可保存记录'
              }
            </Text>
          </Form.Item>
        </Form>
      </Modal>

      {/* 文件任务上传弹窗 */}
      <Modal
        title="上传文件以完成任务"
        open={fileTaskModal.visible}
        onCancel={() => {
          // 强制重置所有状态
          setFileTaskModal({ 
            visible: false, 
            taskIndex: null, 
            uploadedFiles: [], 
            notes: '', 
            isEdit: false 
          });
        }}
                  footer={[
            <Button key="cancel" onClick={() => {
              // 强制重置所有状态
              setFileTaskModal({ 
                visible: false, 
                taskIndex: null, 
                uploadedFiles: [], 
                notes: '', 
                isEdit: false 
              });
            }}>
              取消
            </Button>,
            <Button key="save" type="primary" onClick={saveFileTaskDraft}>
              保存文件
            </Button>
          ]}
        width={600}
      >
                <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            请上传相关文件以完成此任务。支持图片、PDF、Word、Excel等格式，文件大小不超过10MB。
          </Text>
        </div>
        
        {/* 使用AttachmentUpload组件替换原来的Upload.Dragger */}
        <AttachmentUpload
          value={fileTaskModal.uploadedFiles}
          onChange={(files) => {
            setFileTaskModal(prev => ({ ...prev, uploadedFiles: files }));
            // 只更新本地状态，不自动保存
          }}
          compact={false}
          showQrButton={true}
          autoCreateFollowUp={false}
          hideList={false}
          attachToLead={false}
        />
        
        {/* 添加完成备注字段 */}
        <Form.Item
          label="完成备注"
          style={{ marginTop: '16px', marginBottom: '16px' }}
        >
          <Input.TextArea
            placeholder="请描述任务完成情况，例如：完成了什么、有什么注意事项等（可选）"
            value={fileTaskModal.notes || ''}
            onChange={(e) => setFileTaskModal(prev => ({ ...prev, notes: e.target.value }))}
            rows={4}
            showCount
            maxLength={500}
          />
        </Form.Item>
      </Modal>

      {/* 新增任务弹窗 */}
      <Modal
        title="新增入职任务"
        open={addTaskModal.visible}
        onCancel={() => setAddTaskModal({ visible: false, availableTasks: [], selectedTasks: [], loading: false })}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => setAddTaskModal({ visible: false, availableTasks: [], selectedTasks: [], loading: false })}
          >
            取消
          </Button>,
          <Button 
            key="add" 
            type="primary" 
            onClick={handleAddTask}
            disabled={!addTaskModal.selectedTasks || addTaskModal.selectedTasks.length === 0}
          >
            添加任务 ({addTaskModal.selectedTasks?.length || 0})
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            请选择要添加的入职任务。系统会根据员工的部门和岗位筛选可用的任务。
          </Text>
        </div>
        
        <Form layout="vertical">
          <Form.Item label="员工信息" style={{ marginBottom: '16px' }}>
            <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <Text strong>{selectedOnboarding?.employee_name}</Text>
              <Text type="secondary" style={{ marginLeft: '8px' }}>
                {selectedOnboarding?.department} - {selectedOnboarding?.position}
              </Text>
            </div>
          </Form.Item>
          
          <Form.Item label="选择任务" required>
            {addTaskModal.loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text type="secondary">正在加载可用任务...</Text>
              </div>
            ) : addTaskModal.availableTasks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text type="secondary">暂无可添加的任务</Text>
              </div>
            ) : (
              <Checkbox.Group
                value={addTaskModal.selectedTasks.map(task => task.id)}
                onChange={(checkedValues) => {
                  const selectedTasks = addTaskModal.availableTasks.filter(task => 
                    checkedValues.includes(task.id)
                  );
                  setAddTaskModal(prev => ({ ...prev, selectedTasks }));
                }}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {addTaskModal.availableTasks.map(task => (
                    <Checkbox key={task.id} value={task.id} style={{ width: '100%' }}>
                      <div style={{ marginLeft: '8px' }}>
                        <div style={{ fontWeight: 'bold' }}>{task.name}</div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {task.description}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                          类型: {task.task_type === 'file' ? '文件任务' : '文字任务'}
                        </div>
                      </div>
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            )}
          </Form.Item>
        </Form>
      </Modal>
      </div>
    </ConfigProvider>
  );
};

export default Onboarding; 